import React, { useState } from 'react';
import { X, Check, Loader2, ExternalLink, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

// ============================================================================
// GOVERNANCE MODAL
// ============================================================================

const GovernanceModal = ({ item, data, onClose, onSave, saving, departmentSelected, departmentName }) => {
  const [formData, setFormData] = useState({
    status: data.status || 'not_assessed',
    lastAssessmentDate: data.lastAssessmentDate || '',
    nextAssessmentDue: data.nextAssessmentDue || '',
    complianceScore: data.complianceScore || '',
    notes: data.notes || '',
    assignedOwner: data.assignedOwner || '',
    ...data
  });

  const Icon = item.icon;

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const statusOptions = [
    { value: 'not_assessed', label: 'Not Assessed', color: '#6b7280' },
    { value: 'non_compliant', label: 'Non-Compliant', color: '#ef4444' },
    { value: 'partial', label: 'Partially Compliant', color: '#f59e0b' },
    { value: 'compliant', label: 'Compliant', color: '#10b981' }
  ];

  return (
    <div className="zta-modal-backdrop" onClick={onClose}>
      <div className="zta-modal zta-modal--governance" onClick={e => e.stopPropagation()}>
        <div className="zta-modal-header">
          <div className="zta-modal-header-content">
            <div className="zta-gov-modal-icon">
              <Icon size={24} />
            </div>
            <div>
              <h2 className="zta-modal-title">{item.name}</h2>
              <p className="zta-modal-subtitle">
                {item.type === 'regulation' && 'Regulatory Requirement'}
                {item.type === 'governance-body' && 'Governance Body'}
                {item.type === 'policy' && 'Security Policy'}
                {item.type === 'framework' && 'Framework'}
                {departmentName && ` · ${departmentName}`}
              </p>
            </div>
          </div>
          <button className="zta-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="zta-modal-body zta-modal-body--scrollable">
          {/* Summary */}
          <div className="zta-gov-summary">
            <p>{item.summary}</p>
          </div>

          {/* Description */}
          <div className="zta-gov-section">
            <h3>Description</h3>
            <p>{item.description}</p>
          </div>

          {/* Requirements */}
          {item.requirements && item.requirements.length > 0 && (
            <div className="zta-gov-section">
              <h3>Key Requirements</h3>
              <ul className="zta-gov-requirements">
                {item.requirements.map((req, i) => (
                  <li key={i}>
                    <CheckCircle size={14} className="zta-req-icon" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* External Links */}
          {item.links && item.links.length > 0 && (
            <div className="zta-gov-section">
              <h3>Resources & Links</h3>
              <div className="zta-gov-links">
                {item.links.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="zta-gov-link"
                  >
                    <ExternalLink size={14} />
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Department-Specific Section */}
          {departmentSelected && (
            <>
              <div className="zta-gov-divider" />
              <div className="zta-gov-section zta-gov-section--editable">
                <h3>
                  <Clock size={16} />
                  {departmentName} Status
                </h3>

                {/* Compliance Status */}
                <div className="zta-modal-field">
                  <label>Compliance Status</label>
                  <div className="zta-status-buttons">
                    {statusOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`zta-status-btn ${formData.status === opt.value ? 'active' : ''}`}
                        onClick={() => handleFieldChange('status', opt.value)}
                        style={{ '--status-color': opt.color }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assessment Dates */}
                <div className="zta-modal-field-row">
                  <div className="zta-modal-field">
                    <label>Last Assessment Date</label>
                    <input
                      type="date"
                      value={formData.lastAssessmentDate}
                      onChange={e => handleFieldChange('lastAssessmentDate', e.target.value)}
                      className="zta-modal-input"
                    />
                  </div>
                  <div className="zta-modal-field">
                    <label>Next Assessment Due</label>
                    <input
                      type="date"
                      value={formData.nextAssessmentDue}
                      onChange={e => handleFieldChange('nextAssessmentDue', e.target.value)}
                      className="zta-modal-input"
                    />
                  </div>
                </div>

                {/* Compliance Score */}
                <div className="zta-modal-field-row">
                  <div className="zta-modal-field">
                    <label>Compliance Score (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.complianceScore}
                      onChange={e => handleFieldChange('complianceScore', e.target.value)}
                      className="zta-modal-input"
                      placeholder="0-100"
                    />
                  </div>
                  <div className="zta-modal-field">
                    <label>Assigned Owner</label>
                    <input
                      type="text"
                      value={formData.assignedOwner}
                      onChange={e => handleFieldChange('assignedOwner', e.target.value)}
                      className="zta-modal-input"
                      placeholder="e.g. John Smith"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="zta-modal-field">
                  <label>Notes & Findings</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => handleFieldChange('notes', e.target.value)}
                    placeholder="Assessment notes, findings, remediation plans..."
                    className="zta-modal-textarea"
                    rows={4}
                  />
                </div>
              </div>
            </>
          )}

          {!departmentSelected && (
            <div className="zta-gov-no-dept">
              <AlertTriangle size={16} />
              Select a department to track compliance status
            </div>
          )}
        </div>

        <div className="zta-modal-footer">
          <button className="zta-modal-btn zta-modal-btn--secondary" onClick={onClose}>
            Close
          </button>
          {departmentSelected && (
            <button
              className="zta-modal-btn zta-modal-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GovernanceModal;
